import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment';
import { ReportsContext, SALES_COUNTRY_COSTA_RICA, SALES_COUNTRY_PANAMA } from '../../../context/report/ReportContext';
import Paginator from '../../../components/ui/Paginator';
import ReportDateRange from '../../../components/ui/ReportDateRange';
import Message from '../../../components/ui/Message';
import { downloadCsv } from '../../../utils/csv';

const PAGE_SIZE = 25;

// Montos: el SP devuelve cada monto en colones (…LOCAL) y en dólares (…DOLARES)
type AmountCurrency = 'LOCAL' | 'DOLARES';

const numberFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const quantityFormatter = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 3
});

const formatAmount = (value: any) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = Number(value);
    return isNaN(num) ? String(value) : numberFormatter.format(num);
};

const formatDateTime = (value: any) => value ? moment(value).format("DD/MM/YYYY HH:mm") : '-';

const statusOptions = [
    { value: -1, label: 'Todos' },
    { value: 0, label: 'Activa' },
    { value: 1, label: 'Anulada' },
    { value: 2, label: 'Pagada' },
];

const statusBadge = (status?: string | null) => {
    const value = String(status ?? '').toLowerCase();
    if (value === 'pagada') return 'bg-success-subtle text-success-emphasis';
    if (value === 'anulada') return 'bg-danger-subtle text-danger-emphasis';
    if (value === 'activa') return 'bg-warning-subtle text-warning-emphasis';
    return 'bg-secondary-subtle text-secondary-emphasis';
};

const isVoided = (inv: Record<string, any>) => String(inv.EstadoFactura ?? '').toLowerCase() === 'anulada';

// Montos del encabezado (sufijo LOCAL / DOLARES)
const invoiceAmounts = [
    { key: 'MONTOGRAVADO', label: 'Gravado' },
    { key: 'MONTOEXENTO', label: 'Exento' },
    { key: 'SUBTOTAL', label: 'Subtotal' },
    { key: 'DESCUENTO', label: 'Descuento' },
    { key: 'IMPUESTOVENTAS', label: 'Impuesto' },
    { key: 'MONTOOTROSCARGOS', label: 'Otros cargos' },
    { key: 'TOTAL', label: 'Total' },
    { key: 'PAGADO', label: 'Pagado' },
    { key: 'SALDO', label: 'Saldo' },
];

// Montos que se muestran en la tabla principal
const tableAmounts = ['SUBTOTAL', 'DESCUENTO', 'IMPUESTOVENTAS', 'MONTOOTROSCARGOS', 'TOTAL', 'PAGADO', 'SALDO'];

// Etiquetas de las columnas del detalle
const detailLabels: Record<string, string> = {
    CodigoProducto: 'Código',
    Producto: 'Producto',
    CodigoCabys: 'CABYS',
    CANTIDAD: 'Cantidad',
    PRECIOUNITARIO: 'Precio unitario',
    PRECIOLOCAL: 'Precio (CRC)',
    MONTOGRAVADOLOCAL: 'Gravado (CRC)',
    MONTOEXENTOLOCAL: 'Exento (CRC)',
    SUBTOTALLOCAL: 'Subtotal (CRC)',
    DESCUENTOLOCAL: 'Descuento (CRC)',
    IMPUESTOVENTASLOCAL: 'Impuesto (CRC)',
    TOTALLOCAL: 'Total (CRC)',
    PRECIODOLARES: 'Precio (USD)',
    MONTOGRAVADODOLARES: 'Gravado (USD)',
    MONTOEXENTODOLARES: 'Exento (USD)',
    SUBTOTALDOLARES: 'Subtotal (USD)',
    DESCUENTODOLARES: 'Descuento (USD)',
    IMPUESTOVENTASDOLARES: 'Impuesto (USD)',
    TOTALDOLARES: 'Total (USD)',
};

const detailTextColumns = ['CodigoProducto', 'Producto', 'CodigoCabys'];

export const SalesReport = () => {

    const { getSalesReport, getSalesDetail, getSalesPaymentTypes } = useContext(ReportsContext);

    // Filtros (el SP toma la fecha final como inclusiva)
    const [dateIni, setDateIni] = useState(moment().format("YYYYMMDD"));
    const [dateEnd, setDateEnd] = useState(moment().format("YYYYMMDD"));
    const [invoiceId, setInvoiceId] = useState('');
    const [status, setStatus] = useState(-1);
    const [paymentType, setPaymentType] = useState('');
    const [paymentTypes, setPaymentTypes] = useState<{ code: string; description: string }[]>([]);

    // País del comercio (lo indica el API): Costa Rica muestra colones y dólares; Panamá solo dólares
    const [country, setCountry] = useState<number>(SALES_COUNTRY_COSTA_RICA);
    const isPanama = country === SALES_COUNTRY_PANAMA;

    const [selectedCurrency, setCurrency] = useState<AmountCurrency>('LOCAL');
    const currency: AmountCurrency = isPanama ? 'DOLARES' : selectedCurrency;
    const [invoices, setInvoices] = useState<Record<string, any>[]>([]);
    const [buscar, setBuscar] = useState('');
    const [pageActual, setPageActual] = useState(1);
    const [hasSearched, setHasSearched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [selectedInvoice, setSelectedInvoice] = useState<Record<string, any> | null>(null);
    const [detail, setDetail] = useState<Record<string, any>[]>([]);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [showAllColumns, setShowAllColumns] = useState(false);

    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        getSalesPaymentTypes().then(resp => {
            setPaymentTypes(resp.paymentTypes);
            if (resp.country) setCountry(resp.country);
        });
    }, []);

    useEffect(() => {
        setPageActual(1);
    }, [buscar]);

    const handleCallback = (start, end) => {
        setDateIni(start.format('YYYYMMDD'));
        setDateEnd(end.format('YYYYMMDD'));
    }

    const handleFilter = async (e?: React.FormEvent) => {
        e?.preventDefault();

        const trimmedInvoice = invoiceId.trim();
        if (trimmedInvoice && !/^\d+$/.test(trimmedInvoice)) {
            setErrorMessage('El número de factura debe ser numérico.');
            return;
        }

        setIsLoading(true);
        try {
            const result = await getSalesReport({
                from: dateIni,
                to: dateEnd,
                invoiceId: trimmedInvoice,
                status,
                paymentType,
            });
            setInvoices(result.invoices);
            if (result.country) setCountry(result.country);
            setHasSearched(true);
            setPageActual(1);
        } catch (error) {
            setErrorMessage(error?.response?.data?.error || 'No se pudo generar el reporte, intente de nuevo.');
        } finally {
            setIsLoading(false);
        }
    }

    const verDetalle = async (inv: Record<string, any>) => {
        setSelectedInvoice(inv);
        setDetail([]);
        setShowAllColumns(false);
        setLoadingDetail(true);
        try {
            const resp = await getSalesDetail(inv.N_FACTURA);
            setDetail(resp.details);
            if (resp.country) setCountry(resp.country);
        } catch (error) {
            setSelectedInvoice(null);
            setErrorMessage('No se pudo obtener el detalle de la factura, intente de nuevo.');
        } finally {
            setLoadingDetail(false);
        }
    };

    const filteredData = useMemo(() => {
        const term = buscar.trim().toLowerCase();
        if (!term) return invoices;
        return invoices.filter((inv) =>
            [inv.N_FACTURA, inv.EstadoFactura, inv.MONEDA, inv.NOTA]
                .some((value) => String(value ?? '').toLowerCase().includes(term))
        );
    }, [invoices, buscar]);

    // Resumen del período (sin facturas anuladas)
    const summary = useMemo(() => {
        const valid = filteredData.filter(inv => !isVoided(inv));
        const sum = (key: string) => valid.reduce((acc, inv) => acc + Number(inv[`${key}${currency}`] ?? 0), 0);
        const totals: Record<string, number> = {};
        tableAmounts.forEach(key => { totals[key] = sum(key); });
        return {
            count: valid.length,
            voided: filteredData.length - valid.length,
            totals,
        };
    }, [filteredData, currency]);

    const totalRecords = filteredData.length;
    const totalPage = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
    const pageItems = filteredData.slice((pageActual - 1) * PAGE_SIZE, pageActual * PAGE_SIZE);
    const startIndex = totalRecords === 0 ? 0 : (pageActual - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(pageActual * PAGE_SIZE, totalRecords);

    const currencyLabel = currency === 'LOCAL' ? 'CRC' : 'USD';

    const handleDownload = () => {
        const rows = invoices.map((inv) => ({
            "Factura": inv.N_FACTURA,
            "Fecha": formatDateTime(inv.FECHA),
            "Estado": inv.EstadoFactura,
            "Moneda": inv.MONEDA,
            "Gravado (CRC)": inv.MONTOGRAVADOLOCAL,
            "Exento (CRC)": inv.MONTOEXENTOLOCAL,
            "Subtotal (CRC)": inv.SUBTOTALLOCAL,
            "Descuento (CRC)": inv.DESCUENTOLOCAL,
            "Impuesto (CRC)": inv.IMPUESTOVENTASLOCAL,
            "Otros cargos (CRC)": inv.MONTOOTROSCARGOSLOCAL,
            "Total (CRC)": inv.TOTALLOCAL,
            "Pagado (CRC)": inv.PAGADOLOCAL,
            "Saldo (CRC)": inv.SALDOLOCAL,
            "Gravado (USD)": inv.MONTOGRAVADODOLARES,
            "Exento (USD)": inv.MONTOEXENTODOLARES,
            "Subtotal (USD)": inv.SUBTOTALDOLARES,
            "Descuento (USD)": inv.DESCUENTODOLARES,
            "Impuesto (USD)": inv.IMPUESTOVENTASDOLARES,
            "Otros cargos (USD)": inv.MONTOOTROSCARGOSDOLARES,
            "Total (USD)": inv.TOTALDOLARES,
            "Pagado (USD)": inv.PAGADODOLARES,
            "Saldo (USD)": inv.SALDODOLARES,
            "Cambio": inv.CAMBIO,
            "Nota": inv.NOTA,
        }));

        // Panamá: solo los montos en dólares
        downloadCsv('ReporteFacturas.csv', isPanama
            ? rows.map(row => Object.fromEntries(Object.entries(row).filter(([key]) => !key.includes('(CRC)'))))
            : rows);
    }

    // Columnas del detalle: principales en la moneda elegida, o todas
    const detailColumns = detail.length === 0 ? [] : showAllColumns
        ? Object.keys(detail[0]).filter(key => !(isPanama && key.endsWith('LOCAL')))
        : ['CodigoProducto', 'Producto', 'CANTIDAD', 'PRECIOUNITARIO', `SUBTOTAL${currency}`, `DESCUENTO${currency}`, `IMPUESTOVENTAS${currency}`, `TOTAL${currency}`]
            .filter(key => key in detail[0]);

    const renderBody = () => {
        if (isLoading) {
            return (
                <tr><td colSpan={12} className="text-center py-4">
                    <span className="spinner-border spinner-border-sm me-2" /> Generando reporte...
                </td></tr>
            );
        }
        if (!hasSearched) {
            return (
                <tr><td colSpan={12} className="text-center py-4 text-muted">
                    <i className="bi bi-funnel me-2" />Seleccione los filtros y presione "Generar reporte".
                </td></tr>
            );
        }
        if (pageItems.length === 0) {
            return (
                <tr><td colSpan={12} className="text-center py-4 text-muted">
                    {buscar ? 'No se encontraron facturas para la búsqueda.' : 'No hay facturas para los filtros seleccionados.'}
                </td></tr>
            );
        }
        return pageItems.map((inv, i) => {
            const voided = isVoided(inv);
            return (
                <tr key={`${inv.N_FACTURA}-${i}`} className={voided ? 'text-muted' : ''}>
                    <td className="fw-medium">{inv.N_FACTURA}</td>
                    <td style={styles.nowrap}>{formatDateTime(inv.FECHA)}</td>
                    <td><span className={`badge rounded-pill ${statusBadge(inv.EstadoFactura)}`}>{inv.EstadoFactura || '-'}</span></td>
                    <td>{inv.MONEDA || '-'}</td>
                    {tableAmounts.map(key => (
                        <td
                            key={key}
                            className={`text-end ${key === 'TOTAL' ? 'fw-semibold' : ''} ${voided ? 'text-decoration-line-through' : ''}`}
                            style={styles.nowrap}
                        >
                            {formatAmount(inv[`${key}${currency}`])}
                        </td>
                    ))}
                    <td style={{ textAlign: 'center', width: 90, whiteSpace: 'nowrap' }}>
                        <button type="button" className="btn btn-sm btn-primary" title="Ver detalle" onClick={() => verDetalle(inv)}>
                            <i className="bi bi-eye" />
                        </button>
                        {inv.NOTA && (
                            <i className="bi bi-sticky text-muted ms-2" title={String(inv.NOTA)} />
                        )}
                    </td>
                </tr>
            );
        });
    };

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Reporte de facturas</h3>
                    <p className="text-muted mb-0">Facturas emitidas en el período, con sus montos y detalle de productos.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-outline-secondary" onClick={handleDownload} disabled={invoices.length === 0}>
                        <i className="bi bi-download me-1" /> Descargar CSV
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                    <form className="row g-2 align-items-end" onSubmit={handleFilter}>
                        <div className="col-md-6 col-lg-3">
                            <label className="form-label small text-muted mb-1">Rango de fechas</label>
                            <ReportDateRange onChange={handleCallback} minDate={moment().subtract(1, 'year')} />
                        </div>
                        <div className="col-6 col-md-3 col-lg-2">
                            <label className="form-label small text-muted mb-1">Estado</label>
                            <select className="form-select" value={status} onChange={(e) => setStatus(Number(e.target.value))}>
                                {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                        </div>
                        <div className="col-6 col-md-3 col-lg-2">
                            <label className="form-label small text-muted mb-1">Tipo de pago</label>
                            <select className="form-select" value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
                                <option value="">Todos</option>
                                {paymentTypes.map(pt => <option key={pt.code} value={pt.code}>{pt.description}</option>)}
                            </select>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <label className="form-label small text-muted mb-1">N° factura</label>
                            <input
                                className="form-control"
                                placeholder="Opcional"
                                inputMode="numeric"
                                value={invoiceId}
                                onChange={(e) => setInvoiceId(e.target.value)}
                            />
                            {invoiceId.trim() && <div className="form-text">Al buscar una factura se ignoran las fechas.</div>}
                        </div>
                        <div className="col-md-6 col-lg-2 d-grid">
                            <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                <i className="bi bi-bar-chart me-1" /> Generar reporte
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Resumen */}
            {hasSearched && !isLoading && invoices.length > 0 && (
                <div className="row g-3 mb-3">
                    {[
                        { label: 'Facturas', value: String(summary.count), icon: 'bi-receipt', color: '#0d6efd', bg: '#e7f1ff' },
                        { label: `Total vendido (${currencyLabel})`, value: formatAmount(summary.totals.TOTAL), icon: 'bi-cash-stack', color: '#198754', bg: '#e8f6ee' },
                        { label: `Impuesto (${currencyLabel})`, value: formatAmount(summary.totals.IMPUESTOVENTAS), icon: 'bi-percent', color: '#6f42c1', bg: '#f0ebfa' },
                        { label: 'Anuladas', value: String(summary.voided), icon: 'bi-x-circle', color: '#dc3545', bg: '#fdeaec' },
                    ].map(card => (
                        <div className="col-6 col-lg-3" key={card.label}>
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body d-flex align-items-center gap-3">
                                    <div style={{ ...styles.iconCircle, backgroundColor: card.bg, color: card.color }}>
                                        <i className={`bi ${card.icon}`} />
                                    </div>
                                    <div>
                                        <div className="text-muted small">{card.label}</div>
                                        <div className="fs-5 fw-semibold">{card.value}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Resultados */}
            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    {hasSearched && invoices.length > 0 && (
                        <div className="row g-2 mb-3 align-items-center">
                            <div className="col-md-6">
                                <div className="input-group">
                                    <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                    <input
                                        className="form-control"
                                        placeholder="Buscar por factura, estado, moneda o nota..."
                                        value={buscar}
                                        onChange={(e) => setBuscar(e.target.value)}
                                    />
                                    {buscar && (
                                        <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                            <i className="bi bi-x-lg" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className={`col-md-6 justify-content-md-end ${isPanama ? 'd-none' : 'd-flex'}`}>
                                <div className="btn-group btn-group-sm" role="group" aria-label="Moneda de los montos">
                                    <button type="button" className={`btn ${currency === 'LOCAL' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setCurrency('LOCAL')}>
                                        Colones
                                    </button>
                                    <button type="button" className={`btn ${currency === 'DOLARES' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setCurrency('DOLARES')}>
                                        Dólares
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className='table-responsive'>
                        <table className="table table-hover align-middle mb-0">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Factura</th>
                                    <th>Fecha</th>
                                    <th>Estado</th>
                                    <th>Moneda</th>
                                    <th className="text-end">Subtotal</th>
                                    <th className="text-end">Descuento</th>
                                    <th className="text-end">Impuesto</th>
                                    <th className="text-end" style={styles.nowrap}>Otros cargos</th>
                                    <th className="text-end">Total</th>
                                    <th className="text-end">Pagado</th>
                                    <th className="text-end">Saldo</th>
                                    <th style={{ textAlign: 'center' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>{renderBody()}</tbody>
                            {!isLoading && hasSearched && filteredData.length > 0 && (
                                <tfoot>
                                    <tr className="table-light fw-semibold">
                                        <td colSpan={4}>
                                            Total ({currencyLabel})
                                            {summary.voided > 0 && <span className="text-muted small fw-normal ms-1">sin anuladas</span>}
                                        </td>
                                        {tableAmounts.map(key => (
                                            <td key={key} className="text-end" style={styles.nowrap}>{formatAmount(summary.totals[key])}</td>
                                        ))}
                                        <td />
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                        <span className="text-muted small">
                            {!isLoading && totalRecords > 0 ? `Mostrando ${startIndex}-${endIndex} de ${totalRecords} facturas` : ''}
                        </span>
                        {totalPage > 1 && !isLoading ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} /> : <div />}
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {/* Modal de detalle */}
            {selectedInvoice && (
                <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }} onClick={() => setSelectedInvoice(null)}>
                    <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: "min(1100px, 95vw)" }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content border-0">

                            <div className="modal-header">
                                <div>
                                    <h5 className="modal-title mb-1">Factura #{selectedInvoice.N_FACTURA}</h5>
                                    <div className="d-flex align-items-center gap-2 small text-muted">
                                        <span className={`badge rounded-pill ${statusBadge(selectedInvoice.EstadoFactura)}`}>{selectedInvoice.EstadoFactura || '-'}</span>
                                        <span>{formatDateTime(selectedInvoice.FECHA)}</span>
                                        <span className="fw-semibold text-body">Total ({currencyLabel}) {formatAmount(selectedInvoice[`TOTAL${currency}`])}</span>
                                    </div>
                                </div>
                                <button type="button" className="btn-close" onClick={() => setSelectedInvoice(null)}></button>
                            </div>

                            <div className="modal-body" style={{ maxHeight: "75vh" }}>

                                <div className="row g-3 mb-4">
                                    {/* Datos generales */}
                                    <div className="col-12 col-lg-5">
                                        <div className="border rounded p-3 h-100">
                                            <h6 className="mb-3"><i className="bi bi-receipt me-2 text-primary" />Factura</h6>
                                            <div className="row g-2 small">
                                                <div className="col-6"><div className="text-muted">Moneda</div><div className="fw-medium">{selectedInvoice.MONEDA || '-'}</div></div>
                                                {!isPanama && (
                                                    <div className="col-6"><div className="text-muted">Tipo de cambio</div><div className="fw-medium">{formatAmount(selectedInvoice.TIPO_CAMBIO_VENTA)}</div></div>
                                                )}
                                                <div className="col-6"><div className="text-muted">Cambio entregado</div><div className="fw-medium">{formatAmount(selectedInvoice.CAMBIO)}</div></div>
                                                <div className="col-12"><div className="text-muted">Nota</div><div className="fw-medium text-break">{selectedInvoice.NOTA || '-'}</div></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Montos */}
                                    <div className="col-12 col-lg-7">
                                        <div className="border rounded p-3 h-100">
                                            <h6 className="mb-3"><i className="bi bi-calculator me-2 text-primary" />Montos ({currencyLabel})</h6>
                                            <div className="row g-2 small">
                                                {invoiceAmounts.map(amount => (
                                                    <div className="col-4" key={amount.key}>
                                                        <div className="text-muted">{amount.label}</div>
                                                        <div className={amount.key === 'TOTAL' ? 'fw-bold' : 'fw-medium'}>{formatAmount(selectedInvoice[`${amount.key}${currency}`])}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Líneas */}
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0"><i className="bi bi-list-ul me-2 text-primary" />Productos</h6>
                                    {detail.length > 0 && (
                                        <div className="form-check form-switch mb-0">
                                            <input className="form-check-input" type="checkbox" id="salesShowAllColumns" checked={showAllColumns} onChange={(e) => setShowAllColumns(e.target.checked)} />
                                            <label className="form-check-label small" htmlFor="salesShowAllColumns">Ver todas las columnas</label>
                                        </div>
                                    )}
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-sm table-hover align-middle mb-0">
                                        <thead>
                                            <tr className="text-muted small text-uppercase">
                                                {detailColumns.map(key => (
                                                    <th key={key} className={detailTextColumns.includes(key) ? '' : 'text-end'} style={{ whiteSpace: 'nowrap', minWidth: key === 'Producto' ? 220 : undefined }}>
                                                        {detailLabels[key] ?? key}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loadingDetail ? (
                                                <tr><td colSpan={Math.max(detailColumns.length, 1)} className="text-center py-4">
                                                    <span className="spinner-border spinner-border-sm me-2" /> Cargando detalle...
                                                </td></tr>
                                            ) : detail.length === 0 ? (
                                                <tr><td colSpan={Math.max(detailColumns.length, 1)} className="text-center py-4 text-muted">
                                                    La factura no tiene líneas de detalle.
                                                </td></tr>
                                            ) : (
                                                detail.map((line, idx) => (
                                                    <tr key={idx}>
                                                        {detailColumns.map(key => (
                                                            <td key={key} className={detailTextColumns.includes(key) ? '' : 'text-end'} style={detailTextColumns.includes(key) ? undefined : styles.nowrap}>
                                                                {detailTextColumns.includes(key)
                                                                    ? (key === 'Producto'
                                                                        ? <><div className="fw-medium">{line.Producto}</div>{!showAllColumns && line.CodigoCabys && <div className="text-muted small">CABYS {line.CodigoCabys}</div>}</>
                                                                        : String(line[key] ?? '-'))
                                                                    : key === 'CANTIDAD'
                                                                        ? quantityFormatter.format(Number(line[key] ?? 0))
                                                                        : formatAmount(line[key])}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setSelectedInvoice(null)}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {errorMessage.length > 0 && (
                <Message type='error' message={errorMessage} closeDialog={() => setErrorMessage('')} handleOk={() => setErrorMessage('')} backgroundColor='rgba(0,0,0,0.4)' />
            )}
        </div>
    );
}


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
    nowrap: {
        whiteSpace: 'nowrap' as const,
    },
    iconCircle: {
        width: 44,
        height: 44,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 20,
        flexShrink: 0,
    },
};
